import {HttpErrorResponse} from '@angular/common/http';
import {CruiseControlStatusInterface} from '../../../../../model/cruise-control-status.interface';

const noCommandStatusCode: number = 404;
const noCommandTextNotFound: string = 'not found';
const noCommandTextNoCommand: string = 'no command';
const hungCommandText: string = 'hung command';
const resultPlaceholderCssClass: string = 'result-placeholder';
const resultSuccessCssClass: string = 'success-text';
const resultErrorCssClass: string = 'error-text';
const statusQueuedCssClass: string = 'status-pill-queued';
const statusInProgressCssClass: string = 'status-pill-in-progress';
const statusDoneCssClass: string = 'status-pill-done';
const statusFailedCssClass: string = 'status-pill-failed';
const statusCancelledCssClass: string = 'status-pill-cancelled';
const invalidCount: number = -1;
const offerSetsCreatedSuffix: string = 'offer sets created';

export const cruiseControlCommandState = {
  queued: 'queued',
  inProgress: 'in_progress',
  done: 'done',
  failed: 'failed',
  cancelled: 'cancelled',
  idle: 'idle'
} as const;

export const cruiseControlLocalRunPhase = {
  idle: 'idle',
  queuing: 'queuing',
  running: 'running'
} as const;

export type CruiseControlCommandState = typeof cruiseControlCommandState[keyof typeof cruiseControlCommandState];
export type CruiseControlLocalRunPhase = typeof cruiseControlLocalRunPhase[keyof typeof cruiseControlLocalRunPhase];

export const statusClassByCommandState: Record<CruiseControlCommandState, string> = {
  [cruiseControlCommandState.idle]: resultPlaceholderCssClass,
  [cruiseControlCommandState.queued]: statusQueuedCssClass,
  [cruiseControlCommandState.inProgress]: statusInProgressCssClass,
  [cruiseControlCommandState.done]: statusDoneCssClass,
  [cruiseControlCommandState.failed]: statusFailedCssClass,
  [cruiseControlCommandState.cancelled]: statusCancelledCssClass
};

export const statusPillVisibilityByCommandState: Record<CruiseControlCommandState, boolean> = {
  [cruiseControlCommandState.idle]: false,
  [cruiseControlCommandState.queued]: true,
  [cruiseControlCommandState.inProgress]: true,
  [cruiseControlCommandState.done]: true,
  [cruiseControlCommandState.failed]: true,
  [cruiseControlCommandState.cancelled]: true
};

export const latestResultClassByCommandState: Record<CruiseControlCommandState, string> = {
  [cruiseControlCommandState.idle]: resultPlaceholderCssClass,
  [cruiseControlCommandState.queued]: resultPlaceholderCssClass,
  [cruiseControlCommandState.inProgress]: resultPlaceholderCssClass,
  [cruiseControlCommandState.done]: resultSuccessCssClass,
  [cruiseControlCommandState.failed]: resultErrorCssClass,
  [cruiseControlCommandState.cancelled]: resultPlaceholderCssClass
};

/**
 * Trims a text-like value and converts null/undefined/blank input to null.
 */
export function normalizeTextValue(value: string | null | undefined): string | null
{
  let normalizedValue: string | null = null;

  if (value !== null && value !== undefined) {
    const trimmedValue: string = `${value}`.trim();
    if (trimmedValue !== '') {
      normalizedValue = trimmedValue;
    }
  }

  return normalizedValue;
}

/**
 * Returns true when a value exists and contains at least one non-whitespace character.
 */
export function hasTextValue(value: string | null | undefined): boolean
{
  let hasValue: boolean = false;

  if (value !== null && value !== undefined && `${value}`.trim() !== '') {
    hasValue = true;
  }

  return hasValue;
}

/**
 * Merges a raw status payload with empty defaults and normalizes field types for state evaluation.
 */
export function normalizeCruiseControlStatus(
  status: CruiseControlStatusInterface | null | undefined,
  emptyStatus: CruiseControlStatusInterface
): CruiseControlStatusInterface
{
  let normalizedStatus: CruiseControlStatusInterface = emptyStatus;

  if (status) {
    normalizedStatus = {
      ...emptyStatus,
      ...status
    };
  }

  normalizedStatus.created = normalizeTextValue(normalizedStatus.created);
  normalizedStatus.started = normalizeTextValue(normalizedStatus.started);
  normalizedStatus.completed = normalizeTextValue(normalizedStatus.completed);
  normalizedStatus.result = normalizeTextValue(normalizedStatus.result);
  normalizedStatus.pending = normalizedStatus.pending === true;
  normalizedStatus.canceled = normalizedStatus.canceled === true;
  normalizedStatus.manualProcess = normalizedStatus.manualProcess === true;

  if (typeof normalizedStatus.adsCreated !== 'number') {
    normalizedStatus.adsCreated = null;
  }

  return normalizedStatus;
}

/**
 * Determines whether the command has a completed timestamp.
 */
export function isStatusCompleted(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isCompleted: boolean = false;

  if (status && hasTextValue(status.completed)) {
    isCompleted = true;
  }

  return isCompleted;
}

/**
 * Determines whether the backend explicitly marks the command as pending.
 */
export function isStatusPending(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isPending: boolean = false;

  if (status && status.pending === true) {
    isPending = true;
  }

  return isPending;
}

/**
 * Determines whether the command has started and is still running (not completed and not canceled).
 */
export function isStatusStartedAndRunning(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isStartedAndRunning: boolean = false;

  if (status && hasTextValue(status.started) && !isStatusCompleted(status) && status.canceled !== true) {
    isStartedAndRunning = true;
  }

  return isStartedAndRunning;
}

/**
 * Determines whether the command is queued (created, not started, not completed, and not canceled).
 */
export function isStatusCreatedAndQueued(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isCreatedAndQueued: boolean = false;

  if (status
    && hasTextValue(status.created)
    && !hasTextValue(status.started)
    && !isStatusCompleted(status)
    && status.canceled !== true) {
    isCreatedAndQueued = true;
  }

  return isCreatedAndQueued;
}

/**
 * Detects the empty/no-history status shape returned for accounts without prior Cruise Control runs.
 */
export function isNoCommandHistoryStatus(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isNoCommandHistory: boolean = false;

  if (status
    && !hasTextValue(status.created)
    && !hasTextValue(status.started)
    && !hasTextValue(status.completed)
    && status.pending !== true
    && status.canceled !== true
    && !hasTextValue(status.result)) {
    isNoCommandHistory = true;
  }

  return isNoCommandHistory;
}

/**
 * Checks whether result text indicates a hung command.
 */
export function hasHungCommandResult(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let hasHungCommand: boolean = false;

  if (status && hasTextValue(status.result)) {
    const normalizedResultText: string = `${status.result}`.toLowerCase();
    if (normalizedResultText.includes(hungCommandText)) {
      hasHungCommand = true;
    }
  }

  return hasHungCommand;
}

/**
 * Determines whether backend status represents an active command that is not completed yet.
 */
export function isBackendCommandActive(status: CruiseControlStatusInterface | null | undefined): boolean
{
  let isActive: boolean = false;

  if (status
    && !isNoCommandHistoryStatus(status)
    && !isStatusCompleted(status)
    && status.canceled !== true
    && !hasHungCommandResult(status)
    && (isStatusPending(status) || hasTextValue(status.started) || hasTextValue(status.created))) {
    isActive = true;
  }

  return isActive;
}

/**
 * Maps normalized backend status to the UI command state with completed-state precedence rules.
 */
export function getCommandState(status: CruiseControlStatusInterface): CruiseControlCommandState
{
  let commandState: CruiseControlCommandState = getCompletedLifecycleCommandState(status);

  if (commandState === cruiseControlCommandState.idle) {
    commandState = getActiveCommandState(status);
  }

  return commandState;
}

/**
 * Resolves the created offer-set count from structured fields and JSON result fallbacks.
 */
export function getOfferSetsCreatedCount(status: CruiseControlStatusInterface): number
{
  let offerSetsCreatedCount: number = invalidCount;

  if (status && typeof status.adsCreated === 'number') {
    offerSetsCreatedCount = status.adsCreated;
  }

  if (offerSetsCreatedCount < 0) {
    const parsedStatusResult: any = getParsedStatusResult(status);
    if (parsedStatusResult && typeof parsedStatusResult.specialsCreated === 'number') {
      offerSetsCreatedCount = parsedStatusResult.specialsCreated;
    }
    if (parsedStatusResult && typeof parsedStatusResult.adsCreated === 'number') {
      offerSetsCreatedCount = parsedStatusResult.adsCreated;
    }
  }

  return offerSetsCreatedCount;
}

/**
 * Builds the "Latest Result" summary text based on command state and status payload details.
 */
export function getLatestResultText(
  commandState: CruiseControlCommandState,
  status: CruiseControlStatusInterface,
  placeholderValue: string,
  latestResultSuccessText: string,
  latestResultFailedText: string
): string
{
  let latestResultText: string = placeholderValue;

  if (commandState === cruiseControlCommandState.done) {
    latestResultText = getDoneLatestResultText(status, latestResultSuccessText);
  }

  if (commandState === cruiseControlCommandState.failed) {
    latestResultText = getFailedLatestResultText(status, latestResultFailedText);
  }

  if (commandState === cruiseControlCommandState.cancelled) {
    latestResultText = getCancelledLatestResultText(status, placeholderValue);
  }

  return latestResultText;
}

/**
 * Resolves completed command states and leaves incomplete statuses as idle for follow-up evaluation.
 */
function getCompletedLifecycleCommandState(status: CruiseControlStatusInterface): CruiseControlCommandState
{
  let commandState: CruiseControlCommandState = cruiseControlCommandState.idle;
  const isCompleted: boolean = isStatusCompleted(status);

  if (status.canceled === true) {
    commandState = cruiseControlCommandState.cancelled;
  }

  if (commandState === cruiseControlCommandState.idle && isCompleted) {
    commandState = getCompletedCommandState(status);
  }

  if (commandState === cruiseControlCommandState.idle && !isCompleted && hasHungCommandResult(status)) {
    commandState = cruiseControlCommandState.failed;
  }

  return commandState;
}

/**
 * Resolves active incomplete command states when completed checks do not apply.
 */
function getActiveCommandState(status: CruiseControlStatusInterface): CruiseControlCommandState
{
  let commandState: CruiseControlCommandState = cruiseControlCommandState.idle;

  if (isStatusStartedAndRunning(status)) {
    commandState = cruiseControlCommandState.inProgress;
  }

  if (commandState === cruiseControlCommandState.idle && isQueuedCommandState(status)) {
    commandState = cruiseControlCommandState.queued;
  }

  return commandState;
}

/**
 * Resolves queued command states from explicit queue indicators and generalized backend active checks.
 */
function isQueuedCommandState(status: CruiseControlStatusInterface): boolean
{
  let isQueuedState: boolean = false;

  if (isStatusPending(status) || isStatusCreatedAndQueued(status) || isBackendCommandActive(status)) {
    isQueuedState = true;
  }

  return isQueuedState;
}

/**
 * Resolves completed command outcomes and marks completed runs as failed when no offer sets were created and errors exist.
 */
function getCompletedCommandState(status: CruiseControlStatusInterface): CruiseControlCommandState
{
  let commandState: CruiseControlCommandState = cruiseControlCommandState.done;

  if (isCompletedCommandFailure(status)) {
    commandState = cruiseControlCommandState.failed;
  }

  return commandState;
}

/**
 * Checks if a completed command result should be treated as failed due to populated errors and zero created offer sets.
 */
function isCompletedCommandFailure(status: CruiseControlStatusInterface): boolean
{
  let isFailure: boolean = false;
  const parsedStatusResult: any = getParsedStatusResult(status);
  const offerSetsCreatedCount: number = getOfferSetsCreatedCount(status);
  const hasErrors: boolean = hasPopulatedErrors(parsedStatusResult);

  if (offerSetsCreatedCount === 0 && hasErrors) {
    isFailure = true;
  }

  return isFailure;
}

/**
 * Builds latest-result text for successful completed commands.
 */
function getDoneLatestResultText(status: CruiseControlStatusInterface, latestResultSuccessText: string): string
{
  let latestResultText: string = latestResultSuccessText;
  const offerSetsCreatedCount: number = getOfferSetsCreatedCount(status);

  if (offerSetsCreatedCount >= 0) {
    latestResultText = `${offerSetsCreatedCount} ${offerSetsCreatedSuffix}`;
  }

  return latestResultText;
}

/**
 * Builds latest-result text for failed commands using structured errors first, then raw result fallback.
 */
function getFailedLatestResultText(status: CruiseControlStatusInterface, latestResultFailedText: string): string
{
  let latestResultText: string = latestResultFailedText;
  const parsedStatusResult: any = getParsedStatusResult(status);
  const hasParsedErrors: boolean = hasParsedResultErrors(parsedStatusResult);

  if (hasParsedErrors) {
    latestResultText = `${parsedStatusResult.errors}`;
  }

  if (!hasParsedErrors && !parsedStatusResult && hasTextValue(status.result)) {
    latestResultText = `${status.result}`;
  }

  return latestResultText;
}

/**
 * Builds latest-result text for cancelled commands from non-empty result text when available.
 */
function getCancelledLatestResultText(status: CruiseControlStatusInterface, placeholderValue: string): string
{
  let latestResultText: string = placeholderValue;

  if (hasTextValue(status.result)) {
    latestResultText = `${status.result}`;
  }

  return latestResultText;
}

/**
 * Checks whether parsed status result includes a truthy errors value.
 */
function hasParsedResultErrors(parsedStatusResult: any): boolean
{
  let hasErrors: boolean = false;

  if (parsedStatusResult && parsedStatusResult.errors) {
    hasErrors = true;
  }

  return hasErrors;
}

/**
 * Returns the latest run timestamp from status, or the placeholder when unavailable.
 */
export function getLatestRunDate(status: CruiseControlStatusInterface, placeholderValue: string): string
{
  let latestRunDate: string = placeholderValue;

  if (hasTextValue(status.created)) {
    latestRunDate = `${status.created}`;
  }

  return latestRunDate;
}

/**
 * Identifies errors that indicate no command exists (404 or known no-command message text).
 */
export function isNoCommandResponseError(error: unknown): boolean
{
  let isNoCommandResponse: boolean = false;
  const errorStatusCode: number | null = getErrorStatusCode(error);

  if (errorStatusCode === noCommandStatusCode) {
    isNoCommandResponse = true;
  }

  if (!isNoCommandResponse) {
    const normalizedErrorMessage: string = getNormalizedErrorMessage(error);
    if (normalizedErrorMessage.includes(noCommandTextNotFound) || normalizedErrorMessage.includes(noCommandTextNoCommand)) {
      isNoCommandResponse = true;
    }
  }

  return isNoCommandResponse;
}

/**
 * Extracts an HTTP status code from supported error shapes, including nested error payloads.
 */
function getErrorStatusCode(error: unknown): number | null
{
  let errorStatusCode: number | null = null;

  if (error instanceof HttpErrorResponse) {
    errorStatusCode = error.status;
  }

  if (errorStatusCode === null && error && typeof error === 'object') {
    const errorAsRecord: Record<string, unknown> = error as Record<string, unknown>;
    if (typeof errorAsRecord.status === 'number') {
      errorStatusCode = errorAsRecord.status;
    }

    if (errorStatusCode === null && errorAsRecord.error && typeof errorAsRecord.error === 'object') {
      const nestedError: Record<string, unknown> = errorAsRecord.error as Record<string, unknown>;
      if (typeof nestedError.status === 'number') {
        errorStatusCode = nestedError.status;
      }
    }
  }

  return errorStatusCode;
}

/**
 * Normalizes unknown error payloads into lowercase message text for keyword matching.
 */
function getNormalizedErrorMessage(error: unknown): string
{
  let errorMessage: string = '';

  if (typeof error === 'string') {
    errorMessage = error;
  }

  if (errorMessage === '' && error instanceof HttpErrorResponse && typeof error.error === 'string') {
    errorMessage = error.error;
  }

  if (errorMessage === '' && error && typeof error === 'object') {
    const errorAsRecord: Record<string, unknown> = error as Record<string, unknown>;
    if (typeof errorAsRecord.error === 'string') {
      errorMessage = errorAsRecord.error;
    }
    if (errorMessage === '' && typeof errorAsRecord.message === 'string') {
      errorMessage = errorAsRecord.message;
    }
  }

  if (errorMessage === '') {
    errorMessage = `${error}`;
  }

  return errorMessage.toLowerCase();
}

/**
 * Parses a JSON result payload when result text contains an object, otherwise returns null.
 */
function getParsedStatusResult(status: CruiseControlStatusInterface): any
{
  let parsedStatusResult: any = null;

  if (status && status.result) {
    const trimmedResult: string = status.result.trim();
    if (trimmedResult.startsWith('{')) {
      try {
        parsedStatusResult = JSON.parse(trimmedResult);
      } catch {
        parsedStatusResult = null;
      }
    }
  }

  return parsedStatusResult;
}

/**
 * Checks whether a parsed result contains non-empty errors data.
 */
function hasPopulatedErrors(parsedStatusResult: any): boolean
{
  let hasErrors: boolean = false;

  if (parsedStatusResult && parsedStatusResult.errors !== null && parsedStatusResult.errors !== undefined) {
    if (Array.isArray(parsedStatusResult.errors)) {
      if (parsedStatusResult.errors.length > 0) {
        hasErrors = true;
      }
    }
    if (!Array.isArray(parsedStatusResult.errors)) {
      if (`${parsedStatusResult.errors}`.trim() !== '') {
        hasErrors = true;
      }
    }
  }

  return hasErrors;
}
